import { useState } from "react";

import {
  Box,
  Divider,
  Title,
  Group,
  TextInput,
  Badge,
  Select,
  Flex,
  MultiSelect
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";

import { CreateMailModal } from "../components/modals/CreateMailModal.tsx";
import { CustomPagination } from "../components/pagination/CustomPagination.tsx";
import { MailsTable } from "../components/tables/MailsTable.tsx";
import { MAX_ELEMENTS_PER_PAGE } from "../constants/constants.ts";
import { MailSearchType } from "../enums/MailSearchType.ts";
import { MailStatus, MailStatusProperties } from "../enums/MailStatus.ts";
import { useError } from "../hooks/useError.ts";
import { useMails } from "../hooks/useMails.ts";
import { ErrorPage } from "./ErrorPage.tsx";

const MAIL_STATUS_OPTIONS = Object.values(MailStatus).map((status) => ({
  value: status,
  label: MailStatusProperties[status].value
}));

export const HomePage = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [searchType, setSearchType] = useState<MailSearchType>(MailSearchType.SUBJECT);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [filteredStatuses, setFilteredStatuses] = useState<string[]>([]);

  const { mails, isLoading, queryError, isError } = useMails(
    currentPage,
    searchType,
    debouncedSearch,
    filteredStatuses
  );

  const { errorMessage } = useError(queryError);

  const handleStatusChange = (statuses: string[]) => {
    setFilteredStatuses(statuses);
    setCurrentPage(0);
  };

  const handleRemoveStatus = (status: string) => {
    setFilteredStatuses(filteredStatuses.filter((s) => s !== status));
    setCurrentPage(0);
  };

  const handleResetFilters = () => {
    setSearchValue("");
    setFilteredStatuses([]);
    setCurrentPage(0);
  };

  const hasActiveFilters = searchValue || filteredStatuses.length > 0;

  return isError ? (
    <ErrorPage
      code={errorMessage.code}
      title={errorMessage.title}
      description={errorMessage.description}
    />
  ) : (
    <>
      <Group mb="md">
        <Title>Mails</Title>
        <CreateMailModal />
      </Group>

      <Divider size="sm" mb="lg" />

      <Flex mb="lg" align="end" gap="xs">
        <Select
          data={[
            { value: MailSearchType.SUBJECT, label: "Subject" },
            { value: MailSearchType.SENDER, label: "Sender" }
          ]}
          value={searchType}
          onChange={(value) => {
            setSearchType(value as MailSearchType);
            setCurrentPage(0);
          }}
          style={{ width: 130 }}
        />

        <TextInput
          placeholder={
            searchType === MailSearchType.SUBJECT
              ? "Search by subject..."
              : "Search by sender email..."
          }
          value={searchValue}
          onChange={(event) => {
            setSearchValue(event.currentTarget.value);
            setCurrentPage(0);
          }}
          style={{ flex: 1 }}
        />

        <div style={{ position: "relative", width: 220 }}>
          <MultiSelect
            placeholder={filteredStatuses.length === 0 ? "Select status" : ""}
            data={MAIL_STATUS_OPTIONS}
            value={filteredStatuses}
            onChange={handleStatusChange}
            clearable
            searchable
            styles={{
              pill: { display: "none" },
              pillsList: { display: "none" }
            }}
            renderOption={({ option, checked }) => (
              <div
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  backgroundColor: checked ? "rgba(0, 0, 0, 0.08)" : "transparent",
                  fontWeight: checked ? 500 : 400,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {option.label}
              </div>
            )}
          />
          <Badge
            size="sm"
            variant={filteredStatuses.length > 0 ? "filled" : "light"}
            color={filteredStatuses.length > 0 ? "blue" : "gray"}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              paddingLeft: 8,
              paddingRight: 8
            }}
          >
            {filteredStatuses.length > 0
              ? `${filteredStatuses.length} selected ${filteredStatuses.length === 1 ? "status" : "statuses"}`
              : "Select status"}
          </Badge>
        </div>
      </Flex>

      {hasActiveFilters && (
        <Flex mb="lg" gap="xs" wrap="wrap" align="center">
          {filteredStatuses.map((status) => {
            const mailStatus = status as MailStatus;
            return (
              <Badge
                key={status}
                color={MailStatusProperties[mailStatus].color}
                variant="light"
                rightSection={<span style={{ marginLeft: 6, cursor: "pointer" }}>×</span>}
                onClick={() => handleRemoveStatus(status)}
                style={{ cursor: "pointer" }}
              >
                {MailStatusProperties[mailStatus].value}
              </Badge>
            );
          })}

          <Badge
            color="red"
            variant="light"
            style={{ cursor: "pointer", marginLeft: 8 }}
            onClick={handleResetFilters}
          >
            Delete all ×
          </Badge>
        </Flex>
      )}

      <Box mx="auto">
        <MailsTable
          mails={mails}
          maxElementsPerPage={MAX_ELEMENTS_PER_PAGE}
          isLoading={isLoading}
        />
        <CustomPagination
          totalPages={mails?.totalPages ?? 0}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </Box>
    </>
  );
};
